// Self-contained client for documents-ext-service — the attachable-documents
// engine (packs, document requests, the Documents Hub). Mirrors
// documentsApi.ts: SAME shared `platform` cluster, base path /documents-ext.
//
// Do not "correct" the host to documents-ext.api.<apex> — per-service
// subdomains are closed (AWS 120-custom-domain cap).

import type {
  BindingItem, BindingSubject, CompanyProfile, DocumentBinding, DocumentContextType, DocumentPack,
  PackItem, RequestItem, RequestParty, RequestStatus, ResolvedBindingItem,
} from '@platform/shared-types';

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveDocumentsExtHost(
  hostname: string,
  stage: 'prod' | 'staging' | 'dev',
  envBase?: string,
): string {
  if (envBase) return envBase;
  // Dev server proxies /documents-ext-api → platform.api.<apiDevBase>/documents-ext.
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/documents-ext-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/documents-ext`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/documents-ext`;
  return `https://platform.api.dev.${apex}/documents-ext`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_DOCUMENTS_EXT_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveDocumentsExtHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

/**
 * `allow404: true` returns `null` for a 404 response instead of throwing —
 * for endpoints where "not found" is a legitimate state (e.g. "never
 * configured yet"), not an error. Every other non-2xx still throws.
 */
function req<T>(path: string, init?: RequestInit): Promise<T>;
function req<T>(path: string, init: RequestInit, opts: { allow404: true }): Promise<T | null>;
async function req<T>(
  path: string,
  init: RequestInit = {},
  opts: { allow404?: boolean } = {},
): Promise<T | null> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (res.status === 404 && opts.allow404) return null;
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.error || ''; } catch { /* non-JSON body */ }
    throw new Error(detail || `documents-ext ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

const q = (params: Record<string, string | number | undefined>): string => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

/* ── shapes ─────────────────────────────────────────────────────────────── */

export interface PackWriteBody {
  name: string;
  description?: string;
  items: PackItem[];
}

/**
 * One request as the VIEW endpoints return it: item states re-derived LIVE
 * against the envelope rows and the acceptance ledger, and `status` computed
 * from the items on every read — nothing here is a stored flag that can drift.
 */
export interface RequestView {
  request: {
    requestId: string;
    tenantId: string;
    context: { type: DocumentContextType; refId: string };
    packId?: string;
    parties: RequestParty[];
    mergeData: Record<string, string>;
    createdAt: string;
    createdBy: string;
  };
  items: RequestItem[];
  status: RequestStatus;
}

export interface RequestListPage {
  requests: RequestView[];
  count: number;
  /** Real pagination — null means the listing is COMPLETE, not truncated. */
  nextToken: string | null;
}

/* ── client ─────────────────────────────────────────────────────────────── */

export interface SendCounterpartyAgreementBody {
  subject: 'vendor' | 'employee' | 'partner' | 'software';
  subjectId: string;
  tenantId: string;
  /** Who countersigns for us; absent uses the company profile's default. */
  companySignatoryId?: string;
  packId?: string;
  documentIds?: string[];
  party: { name: string; email: string };
  fields: Record<string, string>;
  packages?: { name: string; monthlyPriceCents?: number; features?: string[]; limits?: string; quantity?: number }[];
}

export interface SendCounterpartyAgreementResult {
  requestId: string;
  subject: string;
  contextType: string;
  sent: { itemId: string; documentId: string; envelopeId?: string }[];
  /** Items the engine could not send, each naming the merge fields it still wants. */
  notReady: { itemId: string; documentId: string; missingFields: string[] }[];
}

export const documentsExtApi = {
  /**
   * Create AND send a vendor / employee / partner / software agreement in one
   * call. The counterparty's details go in the PROVIDER's shape (`legalName`,
   * not `vendor_legal_name`) — the service prefixes them through the catalog.
   */
  sendCounterpartyAgreement: (body: SendCounterpartyAgreementBody) =>
    req<SendCounterpartyAgreementResult>('/admin/counterparty-agreements/send', {
      method: 'POST', body: JSON.stringify(body),
    }),

  /* packs */
  listPacks: () => req<{ packs: DocumentPack[]; count: number }>('/admin/packs'),

  createPack: (body: PackWriteBody) =>
    req<{ pack: DocumentPack }>('/admin/packs', { method: 'POST', body: JSON.stringify(body) }),

  getPack: (packId: string) =>
    req<{ pack: DocumentPack }>(`/admin/packs/${encodeURIComponent(packId)}`),

  updatePack: (packId: string, body: PackWriteBody) =>
    req<{ pack: DocumentPack }>(`/admin/packs/${encodeURIComponent(packId)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  retirePack: (packId: string) =>
    req<{ packId: string; status: 'retired'; retiredAt: string }>(
      `/admin/packs/${encodeURIComponent(packId)}/retire`, { method: 'POST' },
    ),

  /* requests — the hub */
  listRequests: (params: {
    tenantId?: string; status?: RequestStatus; contextType?: DocumentContextType;
    limit?: number; nextToken?: string;
  } = {}) =>
    req<RequestListPage>(`/admin/document-requests${q(params)}`),

  getRequest: (requestId: string) =>
    req<RequestView>(`/admin/document-requests/${encodeURIComponent(requestId)}`),

  createRequest: (body: {
    tenantId: string;
    context: { type: DocumentContextType; refId: string };
    packId?: string;
    items?: PackItem[];
    parties: RequestParty[];
    mergeData: Record<string, string>;
    send?: boolean;
  }) =>
    req<{ request: RequestView['request']; items: RequestItem[] }>(
      '/admin/document-requests', { method: 'POST', body: JSON.stringify(body) },
    ),

  patchRequestData: (requestId: string, mergeData: Record<string, string>) =>
    req<{ mergeData: Record<string, string>; items: RequestItem[] }>(
      `/admin/document-requests/${encodeURIComponent(requestId)}/data`,
      { method: 'PATCH', body: JSON.stringify({ mergeData }) },
    ),

  sendRequest: (requestId: string) =>
    req<{ sent: RequestItem[]; notReady: RequestItem[] }>(
      `/admin/document-requests/${encodeURIComponent(requestId)}/send`, { method: 'POST' },
    ),

  addRequestItem: (requestId: string, item: PackItem) =>
    req<{ item: RequestItem }>(
      `/admin/document-requests/${encodeURIComponent(requestId)}/items`,
      { method: 'POST', body: JSON.stringify({ item }) },
    ),

  removeRequestItem: (requestId: string, itemId: string, reason: string) =>
    req<{ requestId: string; itemId: string; state: 'removed' }>(
      `/admin/document-requests/${encodeURIComponent(requestId)}/items/${encodeURIComponent(itemId)}/remove`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  /* countersign + executed artifact (spec §7.1) */

  /**
   * The §6.2 render-for-view for the PLATFORM signer: the html IS the
   * artifact the countersignature will bind to, and `presentedSha256` must be
   * sent back verbatim on sign.
   */
  getSigningView: (envelopeId: string) =>
    req<{
      envelope: Record<string, unknown>;
      title: string;
      html: string;
      presentedSha256: string;
      signer: { signerId: string; name: string; status: string } | null;
    }>(`/admin/envelopes/${encodeURIComponent(envelopeId)}/signing-view`),

  countersign: (envelopeId: string, input: {
    kind: 'typed' | 'drawn';
    typedName?: string;
    signatureImagePngBase64?: string;
    presentedSha256: string;
    /** Real UI facts, not courtesies — the certificate records their instants. */
    consentAccepted: boolean;
    scrolledToEnd: boolean;
  }) =>
    req<{ status: string; executed: boolean; artifactKey?: string }>(
      `/admin/envelopes/${encodeURIComponent(envelopeId)}/sign`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /**
   * Presigned, short-lived. 404 `artifact_not_ready` until executed.
   *
   * `inline` SHOWS the PDF (a viewer frame); the default hands the browser a
   * download. The URL is minted per click and dies in minutes, so nothing
   * durable is ever put in front of the browser.
   */
  artifactUrl: (envelopeId: string, opts?: { inline?: boolean }) =>
    req<{ url: string; sha256?: string }>(
      `/admin/envelopes/${encodeURIComponent(envelopeId)}/artifact${
        opts?.inline ? '?disposition=inline' : ''}`,
    ),

  /* company profile — the platform's own legal identity (spec §Company Identity Master) */

  /**
   * The company's own legal identity. A 404 is NOT an error here — it means
   * "never configured", which the screen renders as an empty form rather than
   * an error state.
   */
  async getCompanyProfile(): Promise<{ profile: CompanyProfile | null; configured: boolean }> {
    const res = await req<{ profile: CompanyProfile; configured: boolean }>(
      '/admin/company-profile', { method: 'GET' }, { allow404: true },
    );
    if (!res || res.configured === false) return { profile: null, configured: false };
    return { profile: res.profile, configured: true };
  },

  async saveCompanyProfile(
    input: Omit<CompanyProfile, 'updatedAt' | 'updatedBy'>,
  ): Promise<CompanyProfile> {
    const res = await req<{ profile: CompanyProfile; configured: boolean }>(
      '/admin/company-profile', { method: 'PUT', body: JSON.stringify(input) },
    );
    return res.profile;
  },

  /* bindings — the registry: which documents a subject (device, financing
     term, package, checkout, business type, jurisdiction) owes. See
     `bindings.ts` server-side: a binding row is CONFIG, replaced wholesale
     on PUT (no merge) and genuinely removed on DELETE. */

  listBindings: () =>
    req<{ bindings: DocumentBinding[]; count: number }>('/admin/bindings'),

  /** A 404 here means "never bound" — not an error the caller should throw on. */
  getBinding: (subjectType: string, subjectId: string) =>
    req<{ binding: DocumentBinding }>(
      `/admin/bindings/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}`,
      { method: 'GET' },
      { allow404: true },
    ),

  /**
   * `subjectType`/`subjectId` travel in the PATH only — the server derives
   * them from there and ignores any copy in the body. `updatedAt`/`updatedBy`
   * are server-owned; never send them.
   */
  saveBinding: (subjectType: string, subjectId: string, body: { items: BindingItem[]; note?: string }) =>
    req<{ binding: DocumentBinding }>(
      `/admin/bindings/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  deleteBinding: (subjectType: string, subjectId: string) =>
    req<{ subjectType: string; subjectId: string; deleted: boolean }>(
      `/admin/bindings/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}`,
      { method: 'DELETE' },
    ),

  /** The preview: what a scenario naming these subjects would end up owing — same pure resolver the binder uses. */
  resolveBindings: (subjects: BindingSubject[]) =>
    req<{ items: ResolvedBindingItem[]; subjectCount: number; boundSubjectCount: number }>(
      '/admin/bindings/resolve', { method: 'POST', body: JSON.stringify({ subjects }) },
    ),
};

export default documentsExtApi;
